import React, { useState, useRef } from 'react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import { toast } from 'react-hot-toast';
import { postDatatoServer } from '../../utils/apiBase';

const ImageUploadModal = ({ show, handleClose, username, currentUser }) => {
  const [headerFile, setHeaderFile] = useState(null);
  const [footerFile, setFooterFile] = useState(null);
  const [fileErrors, setFileErrors] = useState({
    header: '',
    footer: '',
  });

  const headerFileRef = useRef(null);
  const footerFileRef = useRef(null);

  const validateFile = (file, type) => {
    const allowedTypes = ['image/jpeg'];
    const maxSize = 5 * 1024 * 1024;

    if (!file) {
      return `${type} image is required`;
    }

    if (!allowedTypes.includes(file.type)) {
      return 'Only JPEG images are allowed';
    }

    if (file.size > maxSize) {
      return 'File size should not exceed 5MB';
    }

    return '';
  };

  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      const renamedFile = new File([file], `${username}_${type}.jpg`, { type: file.type });
      const validationError = validateFile(renamedFile, type);
      
      if (type === 'header') {
        setHeaderFile(validationError ? null : renamedFile);
        setFileErrors((prev) => ({ ...prev, header: validationError }));
        if (validationError) headerFileRef.current.value = '';
      } else {
        setFooterFile(validationError ? null : renamedFile);
        setFileErrors((prev) => ({ ...prev, footer: validationError }));
        if (validationError) footerFileRef.current.value = '';
      }

      if (validationError) {
        toast.error(`${type} image error: ${validationError}`);
      }
    }
  };

  const handleResponse = (res) => {
    if (res?.response && res?.status === 'success') {
      toast.success('Images uploaded successfully');
      handleClose();
      clearFiles();
    } else {
      toast.error('Failed to upload images');
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const headerError = validateFile(headerFile, 'Header');
    const footerError = validateFile(footerFile, 'Footer');

    setFileErrors({
      header: headerError,
      footer: footerError,
    });

    if (!headerError && !footerError) {
      const formData = new FormData();

      if (headerFile) formData.append('files', headerFile);
      if (footerFile) formData.append('files', footerFile);

      formData.append('isToUpdate', true);

      // Log FormData contents
      console.log('FormData contents:');
      for (let pair of formData.entries()) {
        console.log(pair[0] + ': ', pair[1]);
      }

      // Log additional request details
      console.log('Request details:', {
        endpoint: 'user2',
        headers: {
          token: currentUser?.token,
          contentType: 'multipart/form-data'
        },
        files: {
          header: headerFile ? {
            name: headerFile.name,
            size: headerFile.size,
            type: headerFile.type
          } : null,
          footer: footerFile ? {
            name: footerFile.name,
            size: footerFile.size,
            type: footerFile.type
          } : null
        }
      });

      postDatatoServer({
        end_point: 'user2',
        body: formData,
        call_back: handleResponse,
        props: {
          header: true,
          token: currentUser?.token,
          contentType: 'multipart/form-data',
        },
      });
    }
  };


  const clearFiles = () => {
    setHeaderFile(null);
    setFooterFile(null);
    if (headerFileRef.current) headerFileRef.current.value = '';
    if (footerFileRef.current) footerFileRef.current.value = '';
    setFileErrors({ header: '', footer: '' });
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton className="border-0">
        <Modal.Title className="base-color">Upload Report Images</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit}>
          <Row>
            <Col xs={12} className="mb-3">
              <Form.Group>
                <Form.Label>
                  Report Header Image
                  <span className="text-danger"> *</span>
                </Form.Label>
                <Form.Control
                  ref={headerFileRef}
                  type="file"
                  onChange={(e) => handleFileChange(e, 'header')}
                  accept="image/jpeg"
                  isInvalid={!!fileErrors.header}
                  required
                />
                <Form.Control.Feedback type="invalid">{fileErrors.header}</Form.Control.Feedback>
                {headerFile && <small className="text-muted d-block mt-1">Selected file: {headerFile.name}</small>}
              </Form.Group>
            </Col>

            <Col xs={12} className="mb-3">
              <Form.Group>
                <Form.Label>
                  Report Footer Image
                  <span className="text-danger"> *</span>
                </Form.Label>
                <Form.Control
                  ref={footerFileRef}
                  type="file"
                  onChange={(e) => handleFileChange(e, 'footer')}
                  accept="image/jpeg"
                  isInvalid={!!fileErrors.footer}
                  required
                />
                <Form.Control.Feedback type="invalid">{fileErrors.footer}</Form.Control.Feedback>
                {footerFile && <small className="text-muted d-block mt-1">Selected file: {footerFile.name}</small>}
              </Form.Group>
            </Col>
          </Row>

          <div className="d-flex justify-content-end gap-2 mt-3">
            <Button variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" className="base-color-bg login-btn">
              Upload Images
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default ImageUploadModal;
